// Structural shape instead of an app's PhotoWorkflow type: the web and
// mobile apps declare that row type separately, and this package must not
// depend on either.
export interface WorkflowDescriptionLike {
  title?: string | null;
  description?: string | null;
}

// Service data sometimes repeats the title verbatim in the description;
// rendering both prints the same sentence twice, so a duplicate description
// reads as absent. Shared by the web and mobile apps so the two UIs can
// never disagree on when a description is distinct.
export function getDistinctWorkflowDescription(workflow: WorkflowDescriptionLike): string | null {
  const description = workflow.description?.trim();
  if (!description) return null;
  return description === workflow.title?.trim() ? null : description;
}

// Vendor-authored workflow titles are usually phrased as camera instructions
// — "Take a photo of oven interior", "CLEAN-PLEASE TAKE PHOTO OF SHOWER",
// "PAINT - Please take a photo of Walls". That phrasing is right inside the
// capture flow, but a grid or review list repeating "Take a photo of" for
// every row is noise, so labels strip the boilerplate down to the area
// ("Oven interior", "Shower", "Walls"). An optional one-word tag before the
// instruction ("CLEAN-", "PAINT -") is boilerplate too — the service name is
// already on screen. Titles not phrased as instructions pass through
// unchanged.
const INSTRUCTION_PREFIX =
  /^(?:[a-z]+\s*-+\s*)?(?:please\s+)?take\s+(?:a\s+|an\s+|another\s+)?(?:interior\s+)?photos?\s+of\s+(?:the\s+)?/i;

export function getWorkflowAreaLabel(title?: string | null): string {
  const trimmed = title?.trim() ?? "";
  const area = trimmed.replace(INSTRUCTION_PREFIX, "").trim();
  if (!area || area === trimmed) return trimmed;
  // Areas authored in all caps ("SHOWER") read as shouting once the
  // instruction is gone; mixed-case areas keep their casing.
  const cased = area === area.toUpperCase() ? area.toLowerCase() : area;
  return cased.charAt(0).toUpperCase() + cased.slice(1);
}
